/**
 * createHttpAnalyticsTransport — Phase 28 H4.
 *
 * Builds a transport adapter `{track(envelope)}` that POSTs each emitted
 * analytics envelope as JSON to a configured ingest endpoint. Designed
 * to be passed to `createMarketingAnalytics` (and, by extension, to
 * `<AnalyticsProvider>` via `createMarketingApp({analytics:{transport,...}})`).
 *
 * Contract:
 *   - Synchronous track(): returns immediately. Promise rejection / non-2xx
 *     surface through the REQUIRED `onError` callback (NEVER throws from
 *     the track() call site). A factory built without one refuses by name
 *     (`requireOnError`): a failed send is reported, never swallowed.
 *   - `keepalive: true` so emits during page-unload still ship.
 *   - When `fetch` is omitted, falls back to `globalThis.fetch`.
 *
 * @module @puffin-core/marketing-shell/analytics/createHttpAnalyticsTransport
 */

import requireOnError from './requireOnError.js';

/**
 * @typedef {Object} HttpAnalyticsTransportConfig
 * @property {string} endpoint
 *   Absolute or app-relative ingest URL (e.g. '/api/v1/analytics/ingest').
 * @property {typeof fetch} [fetch]
 *   Custom fetch (defaults to `globalThis.fetch`). Useful for tests + SSR.
 * @property {(error: Error) => void} onError
 *   REQUIRED. Receives any network, serialisation or non-2xx error. The app
 *   passes a reporter built on its own logger.
 */

/**
 * Build the transport.
 *
 * @param {HttpAnalyticsTransportConfig} config
 * @returns {{track: (envelope: Object) => void}}
 */
export default function createHttpAnalyticsTransport(config) {
  if (!config || typeof config !== 'object') {
    throw new TypeError('createHttpAnalyticsTransport: config (object) is required');
  }
  const { endpoint, fetch: customFetch, onError } = config;
  if (typeof endpoint !== 'string' || endpoint.length === 0) {
    throw new TypeError('createHttpAnalyticsTransport: config.endpoint (string) is required');
  }

  requireOnError('createHttpAnalyticsTransport', onError);

  return {
    track(envelope) {
      const fetchImpl = customFetch || globalThis.fetch;
      if (typeof fetchImpl !== 'function') {
        onError(new Error('fetch unavailable'));
        return;
      }
      let bodyJson;
      try {
        bodyJson = JSON.stringify(envelope);
      } catch (err) {
        onError(err);
        return;
      }
      let pending;
      try {
        pending = fetchImpl(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: bodyJson,
          keepalive: true,
        });
      } catch (err) {
        // Fetch threw synchronously (rare, e.g. malformed URL in some
        // runtimes). Surface to onError + return.
        onError(err);
        return;
      }
      if (pending && typeof pending.then === 'function') {
        pending
          .then((res) => {
            if (!res || !res.ok) {
              const status = res && typeof res.status === 'number' ? res.status : 'unknown';
              onError(new Error(`analytics ingest non-2xx: ${status}`));
            }
          })
          .catch(onError);
      }
    },
  };
}
